import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Home, UserRound, HeartHandshake, BellRing, MessageSquareText, LogOut } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Header } from "@/components/landing/Header";
import { ContactSection } from "@/components/landing/ContactSection";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useJamaah } from "@/context/JamaahContext";
import { PortalBeranda } from "@/components/portal/PortalBeranda";
import { ProfilSaya } from "@/components/portal/ProfilSaya";
import { DonasiSaya } from "@/components/portal/DonasiSaya";
import { Langganan } from "@/components/portal/Langganan";
import { SaranAspirasi } from "@/components/portal/SaranAspirasi";

const TABS = [
  { id: "beranda", label: "Beranda", icon: Home },
  { id: "donasi", label: "Donasi Saya", icon: HeartHandshake, el: DonasiSaya },
  { id: "langganan", label: "Langganan Notifikasi", icon: BellRing, el: Langganan },
  { id: "profil", label: "Profil Saya", icon: UserRound, el: ProfilSaya },
  { id: "saran", label: "Saran & Aspirasi", icon: MessageSquareText, el: SaranAspirasi },
];

export default function Portal() {
  const { jamaah, logout } = useJamaah();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [tab, setTab] = useState("beranda");

  useEffect(() => {
    api.get("/profile").then((r) => setProfile(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  if (jamaah === null) return <div className="min-h-screen flex items-center justify-center text-emerald-800">Memuat...</div>;
  if (!jamaah) return <Navigate to="/bergabung?tab=masuk" replace />;

  return (
    <div className="min-h-screen bg-background" data-testid="portal-page">
      <Header profile={profile} />
      <section className="emerald-bg pattern-overlay text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex flex-wrap items-end justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="h-20 w-20 rounded-full border-2 border-[#d4af37] bg-white/10 overflow-hidden flex items-center justify-center shrink-0" data-testid="portal-avatar">
              {jamaah.photo_path ? <img src={fileUrl(jamaah.photo_path)} alt={jamaah.name} className="h-full w-full object-cover" /> : <UserRound size={34} className="text-[#d4af37]" />}
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#80b996]">Portal Jamaah</p>
              <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="portal-greeting">Ahlan wa sahlan, {jamaah.name}</h1>
              <p className="mt-2 text-emerald-100/85 text-sm">RT {jamaah.rt || "-"} / RW {jamaah.rw || "-"} · {jamaah.phone}</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => { logout(); navigate("/"); }} className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10" data-testid="portal-logout-btn">
            <LogOut size={15} /> Keluar
          </Button>
        </div>
      </section>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex flex-wrap h-auto gap-1 bg-emerald-50 p-1 mb-8 w-full sm:w-auto">
            {TABS.map((t) => (
              <TabsTrigger key={t.id} value={t.id} className="gap-2 data-[state=active]:bg-[#0d3b2e] data-[state=active]:text-white" data-testid={`portal-tab-${t.id}`}>
                <t.icon size={15} /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="beranda"><PortalBeranda onNavigate={setTab} /></TabsContent>
          {TABS.filter((t) => t.el).map((t) => <TabsContent key={t.id} value={t.id}><t.el /></TabsContent>)}
        </Tabs>
      </main>
      <ContactSection profile={profile} />
    </div>
  );
}
